import { CoinBadgeV2 } from "@components/CoinBadgeV2";
import { PricesModal } from "@components/modals/PricesModal";
import { useCoins } from "@contexts/CoinsContext";
import useAlert from "@hooks/AlertHook";
import { errorPromise } from "@hooks/ApiHook";
import useModal from "@hooks/ModalHook";
import { useTxModal } from "@hooks/TxModalHook";
import { useWasmQuery } from "@hooks/WasmQueryHook";
import { useWasmX } from "@hooks/WasmXHook";
import { CollectionResponse, NftResponse } from "baaanggg";
import React, { useEffect, useState } from "react";
import { AssetRequest } from "wasmx";

export interface Props {
    nft: NftResponse,
    collection: CollectionResponse
}

function NftSellItemBox({ nft, collection }: Props) {
    const { openModal } = useModal()
    const wasmx = useWasmX()
    const alert = useAlert()
    const { getCoin } = useCoins()
    const txModal = useTxModal()

    const [prices, setPrices] = useState<{ amount: number, coinId: string }[]>([])

    const { data: sellInfo, refresh: refreshSellInfo } = useWasmQuery(wasmx => {
        if (!collection) return errorPromise()
        return wasmx.collection.query.sellInfo({
            contract: collection.collectionContract,
            data: {
                token_id: nft.tokenId
            }
        })
    }, [collection])

    useEffect(() => {
        const pricesStr = localStorage.getItem("nftPrices")
        if (pricesStr !== null) setPrices(JSON.parse(pricesStr)[nft.id] ?? [])
    }, [])

    const changePrices = (prices: { amount: number, coinId: string }[]) => {
        setPrices(prices)

        const pricesStr = localStorage.getItem("nftPrices")
        let localPrices: { [key: string]: any } = pricesStr !== null ? JSON.parse(pricesStr) : {}
        localPrices[nft.id] = prices

        localStorage.setItem("nftPrices", JSON.stringify(localPrices))
    }

    const onSell = async () => {
        try {
            if (prices.length === 0) throw new Error("Please set the sell prices first")
            if (!wasmx) throw new Error("Cannot load chain information, try to refresh the page")


            const approveMsg = wasmx.nft.execute.approve({
                contract: collection.nftContract,
                sender: nft.ownerAddress,
                data: {
                    spender: collection.collectionContract,
                    token_id: nft.tokenId
                }
            })

            const sellMsg = wasmx.collection.execute.sell({
                contract: collection.collectionContract,
                sender: nft.ownerAddress,
                data: {
                    prices: prices.map<AssetRequest>(({ amount, coinId }) => {
                        const coin = getCoin(coinId)!
                        if (coin.isCw20) return {
                            token: {
                                address: coin.cw20Address!,
                                amount: amount.toString()
                            }
                        }
                        else return {
                            coin: {
                                amount: amount.toString(),
                                denom: coin.denom!
                            }
                        }
                    }),
                    token_id: nft.tokenId
                }
            })

            await txModal.show(nft.ownerAddress, [approveMsg, sellMsg], `Sell NFT '${nft.name}'`, {
                onSuccess: async (tx) => {
                    alert.success("NFT is now on sale")
                    await refreshSellInfo()
                },
                onError: (er) => alert.failed(er.message)
            })
        } catch (e) {
            if (e instanceof Error) alert.failed(e.message)
            else console.error(e)
        }
    }

    const onCancelSell = async () => {
        try {
            if (!wasmx) throw new Error("Cannot load chain information, try to refresh the page")


            const revokeMsg = wasmx.nft.execute.revoke({
                contract: collection.nftContract,
                sender: nft.ownerAddress,
                data: {
                    spender: collection.collectionContract,
                    token_id: nft.tokenId
                }
            })

            const cancelSellMsg = wasmx.collection.execute.cancelSell({
                contract: collection.collectionContract,
                sender: nft.ownerAddress,
                data: {
                    token_id: nft.tokenId
                }
            })

            await txModal.show(nft.ownerAddress, [revokeMsg, cancelSellMsg], `Cancel Sell NFT '${nft.name}'`, {
                onSuccess: async (tx) => {
                    alert.success("NFT is now not for sale")
                    await refreshSellInfo()
                },
                onError: (er) => alert.failed(er.message)
            })
        } catch (e) {
            if (e instanceof Error) alert.failed(e.message)
            else console.error(e)
        }
    }

    return <div
        className="rounded-2lg border border-jacarta-100 bg-white p-8 dark:border-gray-400 dark:bg-gray-900"
    >

        {
            !sellInfo && <div className="flex flex-wrap">
                {
                    prices.map(price => <div className="mr-2.5 mb-2.5" key={price.coinId}>
                        <CoinBadgeV2 amount={price.amount} coinId={price.coinId} />
                    </div>
                    )
                }

                <button
                    onClick={() => openModal("prices")}
                    className="group mr-2.5 mb-2.5 inline-flex items-center rounded-lg border border-jacarta-100 bg-white px-4 py-3 hover:border-transparent hover:bg-accent hover:text-white dark:border-jacarta-600 dark:bg-jacarta-800 dark:text-white dark:hover:border-transparent dark:hover:bg-jacarta-500">
                    <span className="text-2xs font-medium">Edit Prices</span>
                </button>
            </div>
        }

        {
            sellInfo && <div className="flex flex-wrap">
                {
                    sellInfo.prices.map(price => {
                        const coin = getCoin(price.coin?.denom ?? price.token?.address ?? "");
                        return <div className="mr-2.5 mb-2.5" key={coin?.id}>
                            <CoinBadgeV2 amount={Number(price.coin?.amount ?? price.token?.amount)} coinId={coin?.id} />
                        </div>
                    }
                    )
                }
            </div>
        }



        {
            !sellInfo && <button
                onClick={onSell}
                className="inline-block w-full rounded-full bg-jacarta-500 py-3 px-8 text-center font-semibold text-white transition-all hover:bg-jacarta-600">
                Sell NFT
            </button>
        }

        {
            sellInfo && <button
                onClick={onCancelSell}
                className="inline-block w-full rounded-full bg-jacarta-500 py-3 px-8 text-center font-semibold text-white transition-all hover:bg-jacarta-600">
                Cancel Sell
            </button>
        }

        <PricesModal
            onChange={prices => changePrices(Object.entries(prices).map(([coinId, amount]) => ({ coinId, amount })))}
            prices={prices.reduce((prev, curr) => ({ ...prev, [curr.coinId]: curr.amount }), {})}
        />
    </div>
}

export default NftSellItemBox;